<template>
  <div class="property-picker-panel" role="listbox">
    <button
      v-for="option in staticOptions"
      :key="option.id"
      type="button"
      class="row"
      :class="{ selected: isSelectedPath([option.id]) }"
      role="option"
      @click="$emit('select', [option.id])"
      @mouseenter="scheduleOpen(undefined)"
      @mouseleave="cancelScheduledOpen"
    >
      <span class="label">{{ option.label }}</span>
    </button>

    <div v-for="entry in entries" :key="entry.id" class="entry">
      <button
        type="button"
        class="row"
        :class="{
          selected: entry.selectable && isSelectedPath(pathTo(entry.id)),
          open: openEntryId === entry.id,
        }"
        role="option"
        @click="onEntryClick(entry)"
        @mouseenter="scheduleOpen(entry.target ? entry.id : undefined)"
        @mouseleave="cancelScheduledOpen"
      >
        <span class="label">{{ entry.label }}</span>
        <span v-if="entry.target" class="drill">
          <ChevronRightIcon />
        </span>
      </button>
      <PropertyPickerPanel
        v-if="entry.target && openEntryId === entry.id"
        class="submenu"
        :types="types"
        :database="entry.target"
        :validate="validate"
        :traversable="traversable"
        :base-path="pathTo(entry.id)"
        :model-value="modelValue"
        @select="(path: string[]) => $emit('select', path)"
      />
    </div>

    <div
      v-if="entries.length === 0 && staticOptions.length === 0"
      class="empty"
    >
      No matching properties
    </div>
  </div>
</template>

<script lang="ts">
import {
  computed,
  defineComponent,
  onBeforeUnmount,
  ref,
  type PropType,
} from "vue";

import type {
  GraphDatabase,
  GraphDatabaseDictionary,
} from "@ctrl/controller-rpc";

import {
  asRecordType,
  relationTargetDatabase,
  type RecordSchemaField,
} from "@/components/graph/predicates";
import ChevronRightIcon from "@/components/icons/ChevronRightIcon.vue";

type StaticOption = { id: string; label: string };

type Entry = {
  id: string;
  label: string;
  /** Passes `validate`; clicking the row selects the path ending here. */
  selectable: boolean;
  /** Relation target opened as a submenu; only set on non-selectable fields. */
  target?: GraphDatabase;
};

/**
 * One level of a property-picker menu: the properties of `database`, where
 * fields passing `validate` are selectable end values and other relation
 * fields open the target type as a nested panel of the same component on
 * hover (unless `traversable` is off). Selections bubble up as the full
 * path from the root (`basePath` + the field id).
 */
export default defineComponent({
  name: "PropertyPickerPanel",
  components: { ChevronRightIcon },
  props: {
    /** All known types, used to resolve relation targets. */
    types: {
      type: Object as PropType<GraphDatabaseDictionary>,
      required: false,
      default: undefined,
    },
    /** The type whose properties this panel lists. */
    database: {
      type: Object as PropType<GraphDatabase>,
      required: false,
      default: undefined,
    },
    /** Whether a field is a valid end value of a path. */
    validate: {
      type: Function as PropType<
        (database: GraphDatabase, field: RecordSchemaField) => boolean
      >,
      required: true,
    },
    /** Whether relation fields can be traversed into nested panels. */
    traversable: {
      type: Boolean,
      default: true,
    },
    /**
     * Synthetic options listed first (root panel only); an option whose id
     * matches one of `database`'s properties shadows that property.
     */
    staticOptions: {
      type: Array as PropType<StaticOption[]>,
      default: () => [],
    },
    /** Path of column ids from the root type to this panel's type. */
    basePath: {
      type: Array as PropType<string[]>,
      default: () => [],
    },
    /** The selected path of column ids (or a static option's id). */
    modelValue: {
      type: Array as PropType<string[]>,
      required: true,
    },
  },
  emits: {
    select: (_path: string[]) => true,
  },
  setup(props, { emit }) {
    /** Entry whose submenu is open; only one sibling submenu at a time. */
    const openEntryId = ref<string>();
    let openTimer: ReturnType<typeof setTimeout> | undefined;

    const cancelScheduledOpen = () => {
      if (openTimer === undefined) return;
      clearTimeout(openTimer);
      openTimer = undefined;
    };
    onBeforeUnmount(cancelScheduledOpen);

    /**
     * Open `id`'s submenu (or close the open one, for `undefined`) after a
     * short hover, so passing over a row does not navigate.
     */
    const scheduleOpen = (id: string | undefined) => {
      cancelScheduledOpen();
      if (id === openEntryId.value) return;
      openTimer = setTimeout(() => {
        openEntryId.value = id;
      }, 300);
    };

    const entries = computed<Entry[]>(() => {
      const database = props.database;
      const fields = asRecordType(database?.type)?.fields;
      if (!database || !fields) return [];
      const list: Entry[] = [];
      for (const [id, field] of Object.entries(fields)) {
        if (props.staticOptions.some((option) => option.id === id)) continue;
        const selectable = props.validate(database, field);
        const target =
          selectable || !props.traversable
            ? undefined
            : relationTargetDatabase(props.types, field);
        if (!selectable && !target) continue;
        list.push({
          id,
          label: field.name ?? id,
          selectable,
          ...(target ? { target } : {}),
        });
      }
      return list;
    });

    const pathTo = (id: string): string[] => [...props.basePath, id];

    const isSelectedPath = (path: string[]): boolean =>
      path.length === props.modelValue.length &&
      path.every((id, index) => id === props.modelValue[index]);

    const onEntryClick = (entry: Entry) => {
      if (entry.selectable) {
        emit("select", pathTo(entry.id));
      } else if (entry.target) {
        cancelScheduledOpen();
        openEntryId.value = entry.id;
      }
    };

    return {
      cancelScheduledOpen,
      entries,
      isSelectedPath,
      onEntryClick,
      openEntryId,
      pathTo,
      scheduleOpen,
    };
  },
});
</script>

<style lang="scss" scoped>
.property-picker-panel {
  min-width: 160px;
  width: max-content;
  max-width: 320px;
  box-sizing: border-box;
  display: flex;
  flex-direction: column;
  row-gap: 2px;
  padding: $padding-tooltip;
  border: 1px solid;
  border-radius: 10px;
  box-shadow: $tooltip;

  @include themed() {
    background-color: t($bg-event);
    border-color: t($grey-15);
  }

  button {
    appearance: none;
    background: none;
    border: none;
    padding: 0;
    margin: 0;
    font: inherit;
    text-align: start;
    cursor: default;

    @include themed() {
      color: t($primary);
    }
  }

  svg {
    width: 10px;
    height: 10px;
    flex-shrink: 0;
  }

  .entry {
    position: relative;
    display: flex;
    flex-direction: column;

    .row {
      flex: 1;
    }
  }

  .row {
    display: flex;
    align-items: center;
    column-gap: 8px;
    padding: 5px 8px;
    border-radius: $border-radius-m;
    @extend %font-caption-regular;

    &:hover,
    &.open {
      @include themed() {
        background-color: t($grey-05);
      }
    }

    .label {
      flex: 1;
      min-width: 0;
      white-space: nowrap;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    &.selected {
      @extend %font-caption-medium;

      @include themed() {
        background-color: t($grey-05);
      }
    }

    .drill {
      display: flex;
      align-items: center;
      justify-content: center;
      width: 18px;
      height: 18px;
    }
  }

  .submenu {
    position: absolute;
    top: -$padding-tooltip;
    inset-inline-end: calc(100% + 4px);
  }

  .empty {
    padding: 5px 8px;
    @extend %font-caption-regular;

    @include themed() {
      color: t($grey-50);
    }
  }
}
</style>
